import React from 'react';
import { motion } from 'framer-motion';
import {
  FlaskConical,
  Brain,
  Shield,
  Microscope,
  Sun,
  Globe2,
  BookOpenCheck,
  Sparkles,
  Activity,
} from 'lucide-react';

/**
 * Clinical Research Section — IIY Phytoventra's compliant retelling of the
 * BCM-95® / Curcugreen® evidence base.
 *
 * IMPORTANT (MHRA / ASA compliance):
 * - We describe what published research has investigated, NOT what the
 *   supplement "does" to a person.
 * - Wording is restricted to factual statements about the formulation,
 *   the mechanism of action (P-glycoprotein efflux pathway), and published
 *   pharmacokinetic data.
 * - No outcome claims, no medical claims, no therapeutic indications.
 *
 * Visual identity: IIY-branded, NOT a re-skin of any supplier deck. Card
 * grid, glass-morphism stat row, two-column mechanism explainer, citation
 * footer with peer-reviewed source.
 */

const HEADLINE_STATS = [
  {
    Icon: Activity,
    value: '7\u00d7',
    label: 'Higher relative oral bioavailability',
    sub: 'BCM-95®\u00ae vs unformulated curcumin',
  },
  {
    Icon: FlaskConical,
    value: '8 hr',
    label: 'Active in the body',
    sub: 'Sustained pharmacokinetic profile',
  },
  {
    Icon: BookOpenCheck,
    value: '46+',
    label: 'Human clinical studies',
    sub: 'Across joint, cognitive & metabolic research',
  },
];

const RESEARCH_AREAS = [
  {
    Icon: Activity,
    title: 'Joint Health Research',
    n: '14 studies',
    body:
      'Published human studies have investigated BCM-95®\u00ae in the context of joint comfort, mobility, and osteoarthritis-related research populations.',
  },
  {
    Icon: Brain,
    title: 'Cognitive Health Research',
    n: '7 studies',
    body:
      'A study funded by the Field Neurosciences Institute (Michigan, USA) reported that free curcumin from BCM-95®\u00ae was detectable in cerebellar tissue, supporting blood\u2013brain barrier crossing in animal models.',
  },
  {
    Icon: FlaskConical,
    title: 'Metabolic & NAFLD Research',
    n: '10 studies',
    body:
      'Published trials examining metabolic markers and non-alcoholic fatty liver disease populations, conducted independently of the manufacturer.',
  },
  {
    Icon: Microscope,
    title: 'Inflammatory Response Research',
    n: '11 studies',
    body:
      'Independent and sponsored research examining markers of inflammatory response in rheumatoid arthritis and broader inflammatory contexts.',
  },
];

const SAFETY_PILLARS = [
  { label: 'FDA GRAS Self-Affirmed', sub: 'Generally Recognised As Safe' },
  { label: 'California Prop-65 Compliant', sub: 'Heavy-metal & contaminant tested' },
  { label: '2 Billion+ doses', sub: 'No reported safety issues to date' },
  { label: 'Clinically studied up to 3.6 g/day', sub: 'OECD-guideline in-vitro & in-vivo data' },
];

const ClinicalResearchSection = () => {
  return (
    <section
      className="relative py-20 md:py-28 bg-gradient-to-b from-stone-50 via-white to-emerald-50/30 overflow-hidden"
      id="clinical-research"
      data-testid="clinical-research-section"
    >
      {/* Decorative grid texture */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" aria-hidden="true" style={{
        backgroundImage: 'radial-gradient(#2d5016 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ---------- Section Header ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-12 md:mb-16"
        >
          <span className="inline-block text-[11px] font-semibold tracking-[0.25em] uppercase text-emerald-700 mb-3">
            The Science Behind Our Bottle
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading text-slate-900 leading-tight mb-4">
            One of the World&apos;s Most Extensively Researched <span className="text-emerald-700">Curcumin Formulations.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            BCM-95® is built on the patented <strong>BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup></strong> formulation &mdash; one of the most researched curcumin complexes in the published nutraceutical literature. Below is a compliant, plain-English summary of what that research actually examined.
          </p>
        </motion.div>

        {/* ---------- Headline Stats Strip ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5 mb-16"
          data-testid="clinical-stats-strip"
        >
          {HEADLINE_STATS.map(({ Icon, value, label, sub }) => (
            <div
              key={label}
              className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 md:p-6 hover:shadow-md hover:border-emerald-200 transition-all"
            >
              <Icon className="w-6 h-6 text-emerald-700 mb-3" strokeWidth={1.8} />
              <div className="text-3xl md:text-4xl font-bold text-slate-900 font-heading leading-none tracking-tight">{value}</div>
              <div className="text-[13px] md:text-sm font-semibold text-slate-800 mt-2 leading-tight">{label}</div>
              <div className="text-[11px] md:text-xs text-slate-500 mt-1">{sub}</div>
            </div>
          ))}
        </motion.div>

        {/* ---------- The Bioavailability Problem — Two-column explainer ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-bold font-heading text-slate-900 mb-2.5">
            The bioavailability problem &mdash; and how BCM-95<sup>&reg;</sup> addresses it.
          </h3>
          <p className="text-slate-600 mb-8 max-w-3xl">
            Plain curcumin is one of the most poorly absorbed natural compounds in modern nutrition. Here is why &mdash; and what the patented BCM-95<sup>&reg;</sup> approach does about it.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Plain curcumin column */}
            <div className="bg-stone-100/70 rounded-2xl p-6 md:p-7 border border-stone-200" data-testid="mechanism-plain-curcumin">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">
                <span className="inline-block w-2 h-2 rounded-full bg-stone-400" />
                Plain curcumin extract
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-3">Pumped out before it can act.</h4>
              <p className="text-[14px] text-slate-600 leading-relaxed mb-4">
                The intestinal lining contains <strong>P-glycoprotein (Pgp)</strong> &mdash; a protein that actively pumps foreign molecules (including curcumin) back into the gut lumen before they are absorbed.
              </p>
              <ul className="text-[13px] text-slate-600 space-y-1.5 list-disc pl-5">
                <li>Rapid efflux into the intestine</li>
                <li>Short retention in the bloodstream (t&frac12; ~2.45 hrs)</li>
                <li>Low free-curcumin concentration (C<sub>max</sub> ~34.9 ng/ml)</li>
                <li>Commonly &ldquo;solved&rdquo; by pairing with piperine &mdash; which also inhibits drug-metabolism enzymes (CYP3A4)</li>
              </ul>
            </div>

            {/* BCM-95® column */}
            <div className="bg-emerald-700 text-white rounded-2xl p-6 md:p-7 border border-emerald-800 relative overflow-hidden" data-testid="mechanism-bcm95">
              {/* subtle accent dots */}
              <div className="absolute inset-0 opacity-[0.06] pointer-events-none" aria-hidden="true" style={{
                backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 mb-3">
                  <Sparkles className="w-3.5 h-3.5" />
                  BCM-95<sup>&reg;</sup>
                </div>
                <h4 className="text-lg font-bold mb-3">A single-plant, dual-action blend.</h4>
                <p className="text-[14px] text-white/85 leading-relaxed mb-4">
                  BCM-95<sup>&reg;</sup> recombines the standardised curcuminoid concentrate with the same plant&apos;s essential-oil fraction (the <strong>turmerones</strong>). Turmerones are natural Pgp inhibitors &mdash; they slow the efflux pump and allow more free curcumin to stay in circulation.
                </p>
                <ul className="text-[13px] text-white/90 space-y-1.5 list-disc pl-5 marker:text-amber-300">
                  <li><strong>7&times; higher</strong> relative oral bioavailability vs unformulated curcumin</li>
                  <li>Active in the body for up to <strong>8 hours</strong></li>
                  <li><strong>No piperine, no soy lecithin, no synthetic carriers</strong></li>
                  <li>Single-plant: standardised curcuminoids + turmeric&apos;s own oils</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ---------- Research Areas Card Grid ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-bold font-heading text-slate-900 mb-2.5">
            Research Across Multiple Areas of Health.
          </h3>
          <p className="text-slate-600 mb-8 max-w-3xl text-sm md:text-base">
            A breakdown of the human studies examining BCM-95<sup>&reg;</sup> across multiple therapeutic research contexts. These are summaries of <em>published research focus areas</em> &mdash; not claims of treatment, cure, or therapeutic outcome.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {RESEARCH_AREAS.map(({ Icon, title, n, body }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-slate-200/80 p-5 md:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid={`research-area-${title.toLowerCase().replace(/\W+/g, '-')}`}
              >
                <Icon className="w-6 h-6 text-emerald-700 mb-3.5" strokeWidth={1.7} />
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 mb-1">{n}</div>
                <h4 className="text-base font-bold text-slate-900 mb-2 leading-tight font-heading">{title}</h4>
                <p className="text-[13px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ---------- Safety + Sustainability strip ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10 mb-12"
        >
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-emerald-700 mb-2">
                <Shield className="w-3.5 h-3.5" /> Safety &amp; Sustainability
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-heading text-slate-900 mb-3 leading-tight">
                Quality You Can Trust. Solar-powered. Single-plant.
              </h3>
              <p className="text-[14px] md:text-[15px] text-slate-600 leading-relaxed">
                Beyond efficacy research, the BCM-95<sup>&reg;</sup> supply chain has been independently audited for safety, traceability and environmental impact.
              </p>
            </div>

            <div className="md:col-span-7 grid grid-cols-2 gap-3">
              {SAFETY_PILLARS.map((p) => (
                <div key={p.label} className="bg-stone-50 rounded-xl p-4 border border-stone-200/80">
                  <div className="text-[13px] font-bold text-slate-900 leading-tight">{p.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.sub}</div>
                </div>
              ))}
              <div className="bg-emerald-50/70 rounded-xl p-4 border border-emerald-200/80 col-span-2 flex items-center gap-3">
                <Sun className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="text-[13px] text-slate-700 leading-snug">
                  <strong>100% solar-powered manufacture</strong> at an ISO 9001 / 14001 / 22000 facility, with 20+ year direct relationships with named farmer communities. <Globe2 className="w-3.5 h-3.5 inline -mt-0.5 text-emerald-700" /> Used by curcumin brands in 70+ countries.
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ---------- Citation Footer ---------- */}
        <div className="text-[11px] md:text-[12px] text-slate-500 leading-relaxed max-w-4xl" data-testid="clinical-citation-footer">
          <strong className="text-slate-700">Key reference:</strong> Antony B, Merina B, Iyer VS, Judy N, Lennertz K, Joyal S. <em>A pilot cross-over study to evaluate human oral bioavailability of BCM-95<sup>&reg;</sup>CG (BCM-95®<sup>TM</sup>), a novel bioenhanced preparation of curcumin.</em> Indian Journal of Pharmaceutical Sciences, July\u2013August 2008, 445&ndash;449. Additional safety, mechanistic, and disease-area research is referenced in our <a href="/bcm-95-explained" className="text-emerald-700 underline">BCM-95<sup>&reg;</sup> guide</a>. BCM-95<sup>&reg;</sup>, Bio-Curcumin<sup>&reg;</sup> and Curcugreen<sup>&reg;</sup> are registered trademarks of Arjuna Natural Pvt. Ltd. BCM-95® is the UK consumer brand from IIY Phytoventra Ltd.
          <br /><br />
          <em>This page describes the formulation and the published research base. BCM-95® is a UK food supplement and is not intended to diagnose, treat, cure or prevent any disease. Always consult your GP or pharmacist before starting a new supplement, particularly if you are pregnant, breastfeeding, or on prescription medication.</em>
        </div>
      </div>
    </section>
  );
};

export default ClinicalResearchSection;
